import { isConversationArchiveInbox } from 'dashboard/helper/conversationArchive';
import { createPendingMessage } from 'dashboard/helper/commons';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';

import ConversationApi from '../../../api/inbox/conversation';
import MessageApi from '../../../api/inbox/message';
import types from '../../mutation-types';
import messageReadActions from './actions/messageReadActions';
import { messagePrefetchCache } from '../../../helper/messagePrefetchCache';
import {
  buildConversationList,
  isOnUnattendedView,
} from './helpers/actionHelpers';
import { normalizeConversationFiltersForCompare } from '../../../helper/conversationFilters';
import {
  WHATSAPP_CONVERSATION_CHANNEL_TYPE,
  buildConversationStatsMetaFromCache,
  cloneConversationCacheList,
  clonePlainValue,
  isConversationCacheOptimizationKeyForInbox,
} from '../../../helper/conversationCache';
import { areSameConversationId } from '../../../helper/conversationIdentity';
import {
  createSafeAbortController,
  getSafeAbortSignal,
} from '../../../helper/abortController';
import {
  addSafeEventListener,
  dispatchSafeWindowCustomEvent,
  removeSafeEventListener,
} from '../../../helper/domEvents';
import {
  conversationNeedsTailRevalidation,
  getConversationTailRevalidationCursor,
  getNewestRenderableMessageId,
  hasLoadedMessagesForActiveConversation,
  isConversationMessageHydrationPending,
  shouldMarkMessagesFetchedAfterPayload,
  shouldUseCachedMessagesForActiveConversation,
} from '../../../helper/conversationMessageLoadState';
import {
  clearSafeInterval,
  scheduleSafeInterval,
} from '../../../helper/browserTimers';
import { getAccountIdFromLocation } from '../../../helper/browserNavigation';
import { waitForDelay } from '../../../helper/delay';
import {
  CONVERSATION_PLACEMENT_SOURCE,
  shouldSuppressRealtimeConversationPlacementForPage,
  shouldSuppressConversationPlacementUpdate,
} from '../../../helper/conversationPlacementState';

let autoOptimizationInterval = null;
let autoOptimizationCleanup = null;
const activeConversationTailRevalidations = new Map();

const getCurrentAccountId = (rootGetters = null) =>
  rootGetters?.getCurrentAccountId ?? null;

const resolveActionAccountId = (rootGetters = null, accountId = null) =>
  accountId ?? getCurrentAccountId(rootGetters) ?? getAccountIdFromLocation();

const areSameAccountId = (first, second) =>
  first != null && second != null && String(first) === String(second);

const isCurrentAccountContext = (rootGetters = null, accountId = null) => {
  if (accountId == null) return false;

  const knownCurrentAccountIds = [
    getCurrentAccountId(rootGetters),
    getAccountIdFromLocation(),
  ].filter((currentAccountId) => currentAccountId != null);

  return (
    knownCurrentAccountIds.length > 0 &&
    knownCurrentAccountIds.every((currentAccountId) =>
      areSameAccountId(currentAccountId, accountId)
    )
  );
};

const buildMessageCacheScope = ({
  accountId = null,
  conversationId = null,
}) => ({
  accountId,
  conversationId,
});

const captureMessageCacheInvalidationToken = (scope) =>
  typeof messagePrefetchCache.captureInvalidationToken === 'function'
    ? messagePrefetchCache.captureInvalidationToken(scope)
    : null;

const isMessageCacheInvalidationTokenCurrent = (scope, token) =>
  token == null ||
  typeof messagePrefetchCache.isInvalidationTokenCurrent !== 'function' ||
  messagePrefetchCache.isInvalidationTokenCurrent(scope, token);

const buildScopedConversationLoadKey = ({
  accountId = null,
  conversationId = null,
}) => `${accountId ?? 'unknown'}:${conversationId ?? 'unknown'}`;

const getConversationById = (state = {}, conversationId = null) =>
  (state.allConversations || []).find((conversation) =>
    areSameConversationId(conversation.id, conversationId)
  );

const getConversationPlacementOperation = (state = {}, conversationId = null) =>
  state?.pendingPlacementOperations?.[String(conversationId)] || null;

const hasRenderableConversationMessageId = (
  conversation = null,
  messageId = null
) =>
  messageId != null &&
  (conversation?.messages || []).some(
    (message) =>
      !isConversationMessageHydrationPending(message) &&
      message?.id != null &&
      String(message.id) === String(messageId)
  );

const getInclusiveAfterCursor = (messageId) => {
  const numericMessageId = Number(messageId);
  return Number.isFinite(numericMessageId) ? numericMessageId - 1 : messageId;
};

// Re-read every persisted message currently loaded, including the oldest one.
// Pending local sends have non-numeric IDs and must not become API cursors.
const getMessageRefreshCursor = (conversation) => {
  const oldestId = (conversation.messages || []).reduce((oldest, message) => {
    const id = Number(message.id);
    if (!Number.isSafeInteger(id) || id <= 0) return oldest;
    return oldest === null ? id : Math.min(oldest, id);
  }, null);
  return oldestId === null ? null : oldestId - 1;
};

const readCachedMessageSnapshot = async (scope) => {
  const memorySnapshot = messagePrefetchCache.get(scope);
  if (memorySnapshot || typeof messagePrefetchCache.restore !== 'function') {
    return memorySnapshot;
  }

  try {
    return await messagePrefetchCache.restore(scope);
  } catch (_) {
    return null;
  }
};

const runDedupedMessageRequest = (scope, factory, requestKey = 'messages') => {
  if (typeof messagePrefetchCache.dedupeInFlight === 'function') {
    return messagePrefetchCache.dedupeInFlight(
      { ...scope, requestKey },
      factory
    );
  }
  return Promise.resolve().then(factory);
};

const primeMessageSnapshot = ({ data, lastMessageId = null, ...scope }) => {
  if (typeof messagePrefetchCache.prime === 'function') {
    messagePrefetchCache
      .prime({ ...scope, data, lastMessageId })
      .catch(() => {});
    return;
  }

  messagePrefetchCache.set({ ...scope, data, lastMessageId });
};

const primeHydratedConversationSnapshot = ({
  accountId,
  conversation,
  conversationId,
  meta = {},
}) => {
  if (!conversation) return false;

  const payload = (conversation.messages || [])
    .filter((message) => !isConversationMessageHydrationPending(message))
    .slice(-20);
  if (!payload.length) return false;

  primeMessageSnapshot({
    accountId,
    conversationId,
    data: { meta, payload },
    lastMessageId: getNewestRenderableMessageId(conversation),
  });
  return true;
};

const scheduleConversationTailRevalidation = ({
  accountId,
  conversationId,
  dispatch,
  state,
}) => {
  const conversation = getConversationById(state, conversationId);
  if (!conversationNeedsTailRevalidation(conversation)) return false;

  const runRevalidation = (attempt = 0) => {
    let request;
    try {
      request = dispatch('revalidateConversationTail', {
        accountId,
        conversationId,
      });
    } catch (error) {
      return attempt < 2
        ? runRevalidation(attempt + 1)
        : Promise.resolve({ error, status: 'failed' });
    }

    return Promise.resolve(request)
      .then((result) =>
        result?.status === 'partial' && attempt < 2
          ? runRevalidation(attempt + 1)
          : result
      )
      .catch((error) =>
        attempt < 2 ? runRevalidation(attempt + 1) : { error, status: 'failed' }
      );
  };

  runRevalidation().catch(() => {});
  return true;
};

const getLabelsFromConversation = (conversation = {}) => {
  if (!conversation) {
    return [];
  }

  if (Array.isArray(conversation.labels) && conversation.labels.length) {
    if (typeof conversation.labels[0] === 'string') {
      return conversation.labels;
    }

    return conversation.labels
      .map((label) => {
        if (!label) {
          return null;
        }
        if (typeof label === 'string') {
          return label;
        }
        return label.title || label.name || null;
      })
      .filter(Boolean);
  }

  if (
    Array.isArray(conversation.label_list) &&
    conversation.label_list.length
  ) {
    // Normalize label_list to an array of strings even if it contains objects
    return conversation.label_list
      .map((label) => {
        if (!label) {
          return null;
        }
        if (typeof label === 'string') {
          return label;
        }
        return label.title || label.name || null;
      })
      .filter(Boolean);
  }

  if (typeof conversation.label_list === 'string') {
    return conversation.label_list
      .split(',')
      .map((label) => label.trim())
      .filter(Boolean);
  }

  return [];
};

/**
 * Per la vista dashboard (Tutte le conversazioni), le email devono avere
 * una label di tipo "inbox" (Posta in arrivo) per essere mostrate.
 * Replica il filtro server di conversation_finder.rb:152-211.
 */
const INBOX_LABEL_TITLES = ['inbox', 'posta in arrivo', 'posteingang'];

/**
 * Sulla dashboard (Tutte le conversazioni), esclude il canale API.
 * Replica il filtro server ALL_CONVERSATIONS_EXCLUDE_WEB_AND_API=true
 * di conversation_finder.rb:69-86.
 */
const EXCLUDED_CHANNEL_TYPES_ON_DASHBOARD = ['Channel::Api'];

const hasConversationPriority = (conversation) => {
  const priority = conversation.priority ?? conversation.meta?.priority ?? null;
  return priority !== null && priority !== undefined && priority !== '';
};

const shouldBlockExcludedChannelOnDashboard = (
  state,
  conversation,
  rootGetters
) => {
  if (!rootGetters) return false;

  const { currentInbox } = state;
  const isDashboard = !currentInbox || String(currentInbox) === '0';
  if (!isDashboard) return false;

  // Il server (Conversations::Finder::InboxScope) include comunque le
  // conversazioni dei canali API che hanno una priorita': stessa eccezione
  // qui, altrimenti l'evento websocket viene scartato e la conversazione
  // compare solo al refresh (chat e chiamate della demo, chiamate del voice).
  if (hasConversationPriority(conversation)) return false;

  const inboxId =
    conversation.inbox_id ??
    conversation.inboxId ??
    conversation.meta?.inbox_id ??
    conversation.meta?.inboxId ??
    null;
  if (!inboxId) return false;

  const inbox = rootGetters['inboxes/getInbox'](inboxId);
  if (!inbox) return false;

  return EXCLUDED_CHANNEL_TYPES_ON_DASHBOARD.includes(inbox.channel_type);
};

const shouldBlockEmailOnDashboard = (state, conversation, rootGetters) => {
  if (!rootGetters) return false;

  const { currentInbox } = state;
  const isDashboard = !currentInbox || String(currentInbox) === '0';
  if (!isDashboard) return false;

  const inboxId =
    conversation.inbox_id ??
    conversation.inboxId ??
    conversation.meta?.inbox_id ??
    conversation.meta?.inboxId ??
    null;
  if (!inboxId) return false;

  const inbox = rootGetters['inboxes/getInbox'](inboxId);
  if (!inbox || inbox.channel_type !== 'Channel::Email') return false;

  // E' una conversazione email sulla dashboard: verifica label "inbox"
  const inboxLabels = rootGetters['inboxes/getInboxLabels'](inboxId);
  if (!inboxLabels || !inboxLabels.length) return false;

  const inboxLabelIds = inboxLabels
    .filter(
      (l) =>
        l.folder_type === 'inbox' ||
        INBOX_LABEL_TITLES.includes((l.title || '').toLowerCase())
    )
    .map((l) => Number(l.id));

  if (!inboxLabelIds.length) return false;

  const convLabelIds = Array.isArray(conversation.label_ids)
    ? conversation.label_ids.map((id) => Number(id))
    : [];

  // Blocca se la conversazione NON ha una label inbox
  return !inboxLabelIds.some((id) => convLabelIds.includes(id));
};

const shouldRemoveConversationForLabelFilter = (
  filters = {},
  conversation = {}
) => {
  // Prefer numeric label_id when present to avoid ambiguity with duplicate titles
  if (filters && (filters.label_id || filters.labelId)) {
    const requiredId = Number(filters.label_id || filters.labelId);
    if (!Number.isFinite(requiredId)) {
      return false;
    }
    const conversationLabelIds = Array.isArray(conversation.label_ids)
      ? conversation.label_ids.map((id) => Number(id)).filter(Number.isFinite)
      : [];

    // If we have an active ID filter and the conversation has no label_ids, remove it
    if (!conversationLabelIds.length) {
      return true;
    }
    return !conversationLabelIds.includes(requiredId);
  }

  // Fallback: use title-based comparison when only titles are provided in filters
  const activeLabels = filters.labels || filters.label;
  if (!activeLabels) {
    return false;
  }
  const requiredLabels = Array.isArray(activeLabels)
    ? activeLabels
    : [activeLabels];
  if (!requiredLabels.length) {
    return false;
  }
  const conversationLabels = getLabelsFromConversation(conversation);
  if (!conversationLabels.length) {
    return true;
  }
  return !requiredLabels.every((label) => conversationLabels.includes(label));
};

/**
 * Verifica se una conversazione dovrebbe essere aggiunta alla lista corrente.
 * Controlla inbox, label filters, e email inbox label.
 */
const shouldAddConversationToList = (
  state,
  conversation,
  rootGetters = null
) => {
  if (!conversation?.id) {
    return false;
  }

  const { currentInbox, conversationFilters = {} } = state;

  // 1. Controlla filtro INBOX
  const inboxId =
    conversation.inbox_id ??
    conversation.inboxId ??
    conversation.meta?.inbox_id ??
    conversation.meta?.inboxId ??
    null;

  const hasSpecificInbox =
    currentInbox !== null &&
    currentInbox !== undefined &&
    String(currentInbox) !== '0';

  if (hasSpecificInbox && inboxId !== null && inboxId !== undefined) {
    if (Number(currentInbox) !== Number(inboxId)) {
      return false; // Inbox diversa, non aggiungere
    }
  }

  // 2. Controlla filtro LABEL (solo per canali che usano label)
  if (
    shouldRemoveConversationForLabelFilter(conversationFilters, conversation)
  ) {
    return false; // Non corrisponde al filtro label, non aggiungere
  }

  // 3. Dashboard: escludi canali API dalla vista generale.
  if (shouldBlockExcludedChannelOnDashboard(state, conversation, rootGetters)) {
    return false;
  }

  // 4. Dashboard: email inbox devono avere label "inbox" (Posta in arrivo)
  if (shouldBlockEmailOnDashboard(state, conversation, rootGetters)) {
    return false;
  }

  return true;
};

export const hasMessageFailedWithExternalError = (pendingMessage) => {
  const { content_attributes: contentAttributes, status } = pendingMessage;
  const externalError = contentAttributes?.external_error ?? '';
  return status === MESSAGE_STATUS.FAILED && externalError !== '';
};

const applyConversationMessageSnapshot = ({
  commit,
  conversation,
  conversationId,
  direction = 'initial',
  snapshot,
}) => {
  if (!snapshot || !Array.isArray(snapshot.payload)) {
    return false;
  }

  const { meta = {}, payload } = snapshot;
  commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
    id: conversationId,
    data: meta,
  });
  commit(types.SET_PREVIOUS_CONVERSATIONS, {
    id: conversationId,
    data: payload,
    direction,
  });

  const dataFetched = shouldMarkMessagesFetchedAfterPayload({
    conversation,
    payload,
  });
  if (!payload.length && dataFetched) {
    commit(types.SET_ALL_MESSAGES_LOADED, conversationId);
  }
  commit(types.UPDATE_CONVERSATION, {
    id: conversationId,
    dataFetched,
  });
  return dataFetched;
};

const actions = {
  setPaginationLoadingStatus({ commit }, status) {
    commit(types.SET_PAGINATION_LOADING_STATUS, status);
  },

  checkNewConversations: async ({ commit, state }) => {
    try {
      const response = await ConversationApi.getLatestId();
      const latestDbId = response.latest_id;

      const hasNew = state.lastFetchedConversationId
        ? latestDbId > state.lastFetchedConversationId
        : false;

      commit(types.SET_HAS_NEW_CONVERSATIONS, hasNew);
      commit(types.SET_LAST_FETCHED_CONVERSATION_ID, latestDbId);
      return hasNew;
    } catch (error) {
      // Errore nel controllo nuove conversazioni
      return false;
    }
  },

  // Prefetch mirato dei messaggi per una singola conversazione
  prefetchConversationMessages: async (
    { commit, dispatch, rootGetters, state },
    {
      accountId: explicitAccountId = null,
      cacheOnly = false,
      conversationId,
      force = false,
      hydrateStore = true,
    } = {}
  ) => {
    const accountId = resolveActionAccountId(rootGetters, explicitAccountId);
    const scope = buildMessageCacheScope({ accountId, conversationId });
    let cachedSnapshot = null;

    try {
      if (!conversationId || accountId == null) {
        return { status: 'invalid-scope' };
      }
      if (!isCurrentAccountContext(rootGetters, accountId)) {
        return { status: 'stale-account' };
      }

      let conversation = getConversationById(state, conversationId);
      cachedSnapshot = await readCachedMessageSnapshot(scope);
      const shouldPreserveLoadedConversation =
        hasLoadedMessagesForActiveConversation(conversation);
      if (
        cachedSnapshot &&
        hydrateStore &&
        conversation &&
        !shouldPreserveLoadedConversation &&
        isCurrentAccountContext(rootGetters, accountId)
      ) {
        applyConversationMessageSnapshot({
          commit,
          conversation,
          conversationId,
          snapshot: cachedSnapshot,
        });
      }

      // La lista ripristina in anticipo tutte le conversazioni visibili da
      // IndexedDB. Un miss locale non deve trasformarsi in trenta richieste
      // HTTP: il warm-up di rete resta separato e limitato alle candidate più
      // probabili.
      if (cacheOnly) {
        return cachedSnapshot
          ? { data: cachedSnapshot, status: 'cached' }
          : { status: 'cache-miss' };
      }

      if (cachedSnapshot && !force) {
        scheduleConversationTailRevalidation({
          accountId,
          conversationId,
          dispatch,
          state,
        });
        return { data: cachedSnapshot, status: 'cached' };
      }

      // Capture the cache generation immediately before the network request;
      // message updates/deletes invalidate it while the request is in flight.
      const invalidationToken = captureMessageCacheInvalidationToken(scope);
      const data = await runDedupedMessageRequest(
        scope,
        async () => {
          const response = await MessageApi.getPreviousMessages(
            { conversationId },
            { accountId, force: true }
          );
          return response?.data || null;
        },
        'initial'
      );

      if (!data || !isCurrentAccountContext(rootGetters, accountId)) {
        return { data, status: data ? 'stale-account' : 'empty' };
      }

      if (!isMessageCacheInvalidationTokenCurrent(scope, invalidationToken)) {
        return { data, status: 'invalidated' };
      }

      const lastMessageId = getNewestRenderableMessageId({
        messages: data.payload,
      });
      primeMessageSnapshot({
        ...scope,
        data,
        invalidationToken,
        lastMessageId,
      });

      conversation = getConversationById(state, conversationId);
      if (hydrateStore && conversation) {
        applyConversationMessageSnapshot({
          commit,
          conversation,
          conversationId,
          snapshot: data,
        });
        scheduleConversationTailRevalidation({
          accountId,
          conversationId,
          dispatch,
          state,
        });
      }
      return {
        data,
        status: cachedSnapshot ? 'revalidated' : 'prefetched',
      };
    } catch (error) {
      return cachedSnapshot
        ? { data: cachedSnapshot, error, status: 'cached-stale' }
        : { error, status: 'failed' };
    }
  },

  // Prefetch completo di conversazione + messaggi (per search suggestions)
  // Non modifica lo store visibile, salva solo in cache
  prefetchConversationFull: async (
    { state, dispatch, rootGetters },
    { accountId: explicitAccountId = null, conversationId } = {}
  ) => {
    try {
      if (!conversationId) return;

      const accountId = resolveActionAccountId(rootGetters, explicitAccountId);
      const scope = buildMessageCacheScope({ accountId, conversationId });
      if (!isCurrentAccountContext(rootGetters, accountId)) return;

      // Skip se già in cache con dati validi
      if (messagePrefetchCache.hasValid(scope)) {
        return;
      }

      // Skip se la conversazione è già nello store con messaggi caricati
      const existingConv = state.allConversations.find((c) =>
        areSameConversationId(c.id, conversationId)
      );
      if (existingConv?.dataFetched) {
        return;
      }

      // Fetch conversazione e messaggi in parallelo
      const [convResponse] = await Promise.all([
        ConversationApi.show(conversationId),
        dispatch('prefetchConversationMessages', {
          accountId,
          conversationId,
          force: true,
        }),
      ]);

      if (!isCurrentAccountContext(rootGetters, accountId)) return;

      // Salva la conversazione nello store (necessario per setActiveChat)
      if (convResponse?.data) {
        dispatch('updateConversation', convResponse.data);
      }
    } catch (error) {
      // Prefetch failures are intentionally non-blocking.
    }
  },

  // Supporta sia la chiamata legacy (solo conversationId) che la nuova
  // (oggetto con forceAdd/placementSource)
  getConversation: async (
    { commit, dispatch, rootGetters, state },
    payload
  ) => {
    // Normalizza payload: può essere un numero/stringa o un oggetto.
    const isObjectPayload = Boolean(payload && typeof payload === 'object');
    const conversationId = isObjectPayload ? payload.conversationId : payload;
    const forceAdd = isObjectPayload ? payload.forceAdd : false;
    const placementSource = isObjectPayload
      ? payload.placementSource
      : undefined;
    const explicitAccountId = isObjectPayload ? payload.accountId : null;
    const hasPlacementOperationGuard =
      isObjectPayload &&
      Object.prototype.hasOwnProperty.call(
        payload,
        'expectedPlacementOperation'
      );
    const expectedPlacementOperation = hasPlacementOperationGuard
      ? payload.expectedPlacementOperation
      : null;
    const accountId = resolveActionAccountId(rootGetters, explicitAccountId);
    const placementOperationIsCurrent = () =>
      !hasPlacementOperationGuard ||
      getConversationPlacementOperation(state, conversationId) ===
        expectedPlacementOperation;

    try {
      if (
        accountId != null &&
        !isCurrentAccountContext(rootGetters, accountId)
      ) {
        return null;
      }
      if (!placementOperationIsCurrent()) return null;
      const response = await ConversationApi.show(conversationId);
      if (
        accountId != null &&
        !isCurrentAccountContext(rootGetters, accountId)
      ) {
        return null;
      }
      if (!placementOperationIsCurrent()) return null;
      const conversationData = placementSource
        ? {
            ...response.data,
            __placement_source: placementSource,
          }
        : response.data;
      if (forceAdd) {
        // Bypassa il gate - usato quando l'utente vuole esplicitamente vedere una conversazione
        // (es. da risultati ricerca che potrebbero non appartenere all'inbox/label corrente)
        commit(types.UPDATE_CONVERSATION, conversationData);
      } else {
        // Passa attraverso il gate di updateConversation
        // che filtra conversazioni non pertinenti alla vista corrente (es. da WebSocket)
        dispatch('updateConversation', conversationData);
      }
      return conversationData;
    } catch (error) {
      // Ignore error
      return null;
    }
  },

  fetchAllConversations: async (
    { commit, state, dispatch },
    params,
    signal
  ) => {
    let requestPayload = params || {};
    let requestSignal = signal;

    const looksLikeSignal =
      requestPayload &&
      typeof requestPayload.aborted === 'boolean' &&
      typeof requestPayload.addEventListener === 'function';

    if (looksLikeSignal) {
      requestSignal = requestPayload;
      requestPayload = {};
    } else if (requestPayload.signal) {
      const { signal: payloadSignal, ...filters } = requestPayload;
      requestSignal = payloadSignal;
      requestPayload = filters;
    }

    // Creare un AbortController solo se non è stato fornito un signal
    if (!requestSignal) {
      requestSignal = getSafeAbortSignal(createSafeAbortController());
    }
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      let previouslySelectedChatId = null;
      if (state.currentChatWindow && state.currentChatWindow.id) {
        previouslySelectedChatId = state.currentChatWindow.id;
      }

      // Combinare i parametri di stato con quelli passati alla funzione
      const requestParams = { ...state.conversationFilters, ...requestPayload };

      // Fetch delta al reconnect: il backend filtra su updated_at e non pagina,
      // quindi la risposta è un frammento e non può sostituire la lista.
      // La chiave è la stessa che legge il layer API (api/inbox/conversation).
      const isDeltaFetch = Boolean(requestParams.updatedWithin);

      // Il flag vale per questa sola richiesta: toglierlo subito dai filtri
      // persistenti evita che un fetch fallito lo lasci attaccato e trasformi
      // ogni fetch successivo in un merge che non svuota più la lista.
      if (isDeltaFetch) {
        commit(types.REMOVE_PROPERTY_FROM_FILTERS, 'updatedWithin');
      }

      if (requestParams.assignee_id === null) {
        delete requestParams.assignee_id;
      }

      // Pass unread_first flag from store state
      if (state.unreadFirstEnabled) {
        requestParams.unread_first = 'true';
      }

      // Pass has_priority filter from store state.
      // Let the backend apply priority_urgency: priority first, incoming chronology inside each priority.
      if (state.priorityFilterEnabled) {
        requestParams.has_priority = 'true';
        delete requestParams.sortBy;
      }

      const {
        data: { data },
      } = await ConversationApi.get(requestParams, requestSignal);

      if (!isDeltaFetch) {
        commit(types.EMPTY_ALL_CONVERSATION);
      }

      buildConversationList(
        { commit, dispatch },
        requestParams,
        data,
        requestParams.assigneeType,
        { isDeltaFetch }
      );

      if (previouslySelectedChatId) {
        const chatToReselect = state.allConversations.find((chat) =>
          areSameConversationId(chat.id, previouslySelectedChatId)
        );
        if (chatToReselect) {
          dispatch('setActiveChat', { data: chatToReselect, after: null });
        }
      }

      return { data };
    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        // fetchAllConversations: request aborted
      } else {
        // Errore nel fetch delle conversazioni
      }
      throw error;
    } finally {
      commit(types.CLEAR_LIST_LOADING_STATUS);
    }
  },

  fetchFilteredConversations: async ({ commit, dispatch }, params) => {
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const { data } = await ConversationApi.filter(params);

      buildConversationList(
        { commit, dispatch },
        params,
        data,
        'appliedFilters'
      );
    } catch (error) {
      // Errore nel fetch delle conversazioni filtrate
    }
  },

  emptyAllConversations({ commit }) {
    commit(types.EMPTY_ALL_CONVERSATION);
  },

  clearSelectedState({ commit }) {
    commit(types.CLEAR_CURRENT_CHAT_WINDOW);
  },

  fetchPreviousMessages: async ({ commit, rootGetters }, data) => {
    const {
      accountId: explicitAccountId = null,
      direction = data?.after ? 'after' : 'before',
      force = false,
      ...requestData
    } = data;
    const accountId = resolveActionAccountId(rootGetters, explicitAccountId);
    if (!isCurrentAccountContext(rootGetters, accountId)) {
      return { meta: {}, payload: [], status: 'stale-account' };
    }
    const scope = buildMessageCacheScope({
      accountId,
      conversationId: requestData.conversationId,
    });
    const invalidationToken = captureMessageCacheInvalidationToken(scope);
    const {
      data: { meta, payload },
    } = await MessageApi.getPreviousMessages(requestData, {
      accountId,
      force,
    });
    if (!isCurrentAccountContext(rootGetters, accountId)) {
      return { meta, payload, status: 'stale-account' };
    }
    if (!isMessageCacheInvalidationTokenCurrent(scope, invalidationToken)) {
      return { meta, payload, status: 'invalidated' };
    }
    commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
      id: requestData.conversationId,
      data: meta,
    });
    commit(types.SET_PREVIOUS_CONVERSATIONS, {
      id: requestData.conversationId,
      data: payload,
      direction,
    });
    // allMessagesLoaded significa "niente messaggi più VECCHI": un fetch
    // delta (`after`) vuoto non dice nulla sulla storia e non deve fermare
    // la paginazione verso l'alto.
    if (!payload.length && requestData.after == null) {
      commit(types.SET_ALL_MESSAGES_LOADED, requestData.conversationId);
    }
    return { meta, payload, status: 'fetched' };
  },

  // Riallinea in background la coda del thread quando il preview della lista
  // è più nuovo dell'ultimo messaggio completo in store. Un solo fetch delta
  // in volo per conversazione.
  revalidateConversationTail: async (
    { dispatch, rootGetters, state },
    { accountId: explicitAccountId = null, conversationId, maxPages = 20 } = {}
  ) => {
    if (!conversationId) {
      return undefined;
    }
    const accountId = resolveActionAccountId(rootGetters, explicitAccountId);
    let conversation = getConversationById(state, conversationId);
    if (!conversationNeedsTailRevalidation(conversation)) {
      return undefined;
    }

    const loadKey = buildScopedConversationLoadKey({
      accountId,
      conversationId,
    });
    if (activeConversationTailRevalidations.has(loadKey)) {
      return activeConversationTailRevalidations.get(loadKey);
    }

    const revalidationPromise = (async () => {
      let page = 0;
      let lastResponse = null;

      while (page < maxPages) {
        if (!isCurrentAccountContext(rootGetters, accountId)) {
          return { status: 'stale-account' };
        }

        conversation = getConversationById(state, conversationId);
        if (!conversationNeedsTailRevalidation(conversation)) {
          break;
        }

        const after = getConversationTailRevalidationCursor(conversation);
        if (after == null) {
          return dispatch('ensureConversationMessagesLoaded', {
            accountId,
            conversationId,
            force: true,
          });
        }

        // Delta pages are cursor-dependent and must be fetched sequentially.
        // eslint-disable-next-line no-await-in-loop
        lastResponse = await dispatch('fetchPreviousMessages', {
          accountId,
          after,
          before: undefined,
          conversationId,
          direction: 'after',
          force: true,
        });
        if (
          lastResponse?.status === 'stale-account' ||
          lastResponse?.status === 'invalidated'
        ) {
          return lastResponse;
        }
        const payload = lastResponse?.payload || [];
        if (!payload.length) {
          break;
        }

        const nextConversation = getConversationById(state, conversationId);
        const nextAfter =
          getConversationTailRevalidationCursor(nextConversation);
        if (nextAfter == null || nextAfter <= after) {
          break;
        }
        page += 1;
      }

      conversation = getConversationById(state, conversationId);
      if (!isCurrentAccountContext(rootGetters, accountId)) {
        return { status: 'stale-account' };
      }
      primeHydratedConversationSnapshot({
        accountId,
        conversation,
        conversationId,
        meta: lastResponse?.meta || {},
      });

      return {
        pages: page,
        status: conversationNeedsTailRevalidation(conversation)
          ? 'partial'
          : 'revalidated',
      };
    })();
    activeConversationTailRevalidations.set(loadKey, revalidationPromise);
    try {
      return await revalidationPromise;
    } finally {
      activeConversationTailRevalidations.delete(loadKey);
    }
  },

  ensureConversationMessagesLoaded: async (
    { commit, dispatch, rootGetters, state },
    {
      accountId: explicitAccountId = null,
      conversationId,
      after = undefined,
      force = false,
    } = {}
  ) => {
    if (!conversationId) {
      return { status: 'no-conversation' };
    }

    const accountId = resolveActionAccountId(rootGetters, explicitAccountId);
    let conversationInStore = getConversationById(state, conversationId);
    if (!conversationInStore) {
      return { status: 'missing-conversation' };
    }

    const hasExplicitAfter =
      after !== null && after !== undefined && after !== '';
    if (
      !force &&
      hasLoadedMessagesForActiveConversation(conversationInStore) &&
      (!hasExplicitAfter ||
        hasRenderableConversationMessageId(conversationInStore, after))
    ) {
      return { status: 'already-loaded' };
    }

    commit(types.CLEAR_ALL_MESSAGES_LOADED, conversationId);
    const scope = buildMessageCacheScope({ accountId, conversationId });
    // Il memory hit deve restare sincrono: Vue applica così hydration e
    // selezione nello stesso tick e non monta lo spinner per un solo frame.
    let cachedMessages = messagePrefetchCache.get(scope);
    if (!cachedMessages) {
      cachedMessages = await readCachedMessageSnapshot(scope);
    }
    if (!isCurrentAccountContext(rootGetters, accountId)) {
      return { status: 'stale-account' };
    }
    const canUseCachedMessages = shouldUseCachedMessagesForActiveConversation({
      cachedMessages,
      conversation: conversationInStore,
    });

    if (canUseCachedMessages) {
      applyConversationMessageSnapshot({
        commit,
        conversation: conversationInStore,
        conversationId,
        snapshot: cachedMessages,
      });
      const conversationAfterCache =
        getConversationById(state, conversationId) || conversationInStore;
      if (
        !force &&
        (!hasExplicitAfter ||
          hasRenderableConversationMessageId(conversationAfterCache, after))
      ) {
        scheduleConversationTailRevalidation({
          accountId,
          conversationId,
          dispatch,
          state,
        });
        return { status: 'cached', payload: cachedMessages.payload };
      }
    } else if (cachedMessages) {
      messagePrefetchCache.invalidateConversation(scope);
    }

    try {
      let messagesResponse;
      if (hasExplicitAfter) {
        messagesResponse = await dispatch('fetchPreviousMessages', {
          accountId,
          after: getInclusiveAfterCursor(after),
          before: undefined,
          conversationId,
          direction: 'after',
          force: true,
        });
        if (
          messagesResponse?.status === 'stale-account' ||
          messagesResponse?.status === 'invalidated'
        ) {
          return messagesResponse;
        }
      } else {
        const loadResult = await dispatch('prefetchConversationMessages', {
          accountId,
          conversationId,
          force: true,
          hydrateStore: true,
        });
        if (loadResult?.status === 'failed') {
          throw loadResult.error || new Error('Message prefetch failed');
        }
        if (
          loadResult?.status === 'stale-account' ||
          loadResult?.status === 'invalidated'
        ) {
          return loadResult;
        }
        messagesResponse = loadResult?.data || {};
      }
      conversationInStore =
        getConversationById(state, conversationId) || conversationInStore;
      const shouldMarkFetched = shouldMarkMessagesFetchedAfterPayload({
        conversation: conversationInStore,
        payload: messagesResponse?.payload,
      });
      commit(types.UPDATE_CONVERSATION, {
        id: conversationId,
        dataFetched: shouldMarkFetched,
      });
      return {
        payload: messagesResponse?.payload || [],
        status: shouldMarkFetched ? 'fetched' : 'empty-non-final',
      };
    } catch (error) {
      const latestConversation =
        getConversationById(state, conversationId) || conversationInStore;
      const hasUsableMessages =
        hasLoadedMessagesForActiveConversation(latestConversation);
      commit(types.UPDATE_CONVERSATION, {
        id: conversationId,
        dataFetched: hasUsableMessages,
      });
      return { error, status: 'failed' };
    }
  },

  fetchAllAttachments: async ({ commit }, conversationId) => {
    const { data } = await ConversationApi.getAllAttachments(conversationId);
    commit(types.SET_ALL_ATTACHMENTS, {
      id: conversationId,
      data: data.payload,
    });
  },

  syncActiveConversationMessages: async (
    { commit, dispatch, rootGetters, state },
    {
      accountId: explicitAccountId = null,
      conversationId,
      maxPages = 20,
      fullSync = false,
      refreshAfter = null,
    }
  ) => {
    const accountId = resolveActionAccountId(rootGetters, explicitAccountId);
    const syncCursorKey = buildScopedConversationLoadKey({
      accountId,
      conversationId,
    });
    let selectedChat = getConversationById(state, conversationId);
    if (!selectedChat) return { status: 'missing-conversation' };

    try {
      if (!fullSync && conversationNeedsTailRevalidation(selectedChat)) {
        const result = await dispatch('revalidateConversationTail', {
          accountId,
          conversationId,
          maxPages,
        });
        if (
          result?.status === 'stale-account' ||
          result?.status === 'invalidated'
        ) {
          return result;
        }
        if (!isCurrentAccountContext(rootGetters, accountId)) {
          return { status: 'stale-account' };
        }
        selectedChat = getConversationById(state, conversationId);
        const latestHydratedId = getNewestRenderableMessageId(selectedChat);
        if (latestHydratedId != null) {
          commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
            accountId,
            conversationId,
            messageId: latestHydratedId,
          });
        }
        return result;
      }

      let cursor = fullSync
        ? (refreshAfter ?? getMessageRefreshCursor(selectedChat))
        : (state.syncConversationsMessages[syncCursorKey] ??
          getNewestRenderableMessageId(selectedChat));
      if (cursor == null) {
        return dispatch('ensureConversationMessagesLoaded', {
          accountId,
          conversationId,
          force: true,
        });
      }

      let page = 0;
      let lastPageWasFull = false;
      while (page < maxPages) {
        // Catch-up pages are cursor-dependent and must be fetched sequentially.
        // eslint-disable-next-line no-await-in-loop
        const response = await dispatch('fetchPreviousMessages', {
          accountId,
          after: cursor,
          conversationId,
          direction: 'after',
          force: true,
        });
        if (
          response?.status === 'stale-account' ||
          response?.status === 'invalidated'
        ) {
          return response;
        }
        const payload = response?.payload || [];
        lastPageWasFull = payload.length >= 100;
        selectedChat = getConversationById(state, conversationId);
        // Advance from the actual API page, not from the newest ID in store:
        // full resync starts behind already-loaded messages and realtime can
        // also append newer IDs while this request is in flight.
        const nextCursor = getNewestRenderableMessageId({ messages: payload });
        if (nextCursor == null || nextCursor <= cursor) {
          break;
        }
        cursor = nextCursor;
        page += 1;
        if (payload.length < 100) {
          break;
        }
      }

      if (cursor != null && (!fullSync || !lastPageWasFull)) {
        commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
          accountId,
          conversationId,
          messageId: cursor,
        });
      }
      selectedChat = getConversationById(state, conversationId);
      primeHydratedConversationSnapshot({
        accountId,
        conversation: selectedChat,
        conversationId,
      });
      return {
        pages: page,
        status: lastPageWasFull ? 'partial' : 'synced',
        ...(fullSync ? { nextCursor: cursor } : {}),
      };
    } catch (error) {
      return { error, status: 'failed' };
    }
  },

  setConversationLastMessageId: async (
    { commit, rootGetters, state },
    { conversationId }
  ) => {
    const accountId = resolveActionAccountId(rootGetters);
    const { allConversations } = state;
    const selectedChat = allConversations.find((conversation) =>
      areSameConversationId(conversation.id, conversationId)
    );
    if (!selectedChat) return;
    const lastMessageId = getNewestRenderableMessageId(selectedChat);
    if (lastMessageId == null) return;
    commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
      accountId,
      conversationId,
      messageId: lastMessageId,
    });
  },

  async setActiveChat(
    { commit, dispatch, rootGetters, state },
    { data, after }
  ) {
    commit(types.SET_CURRENT_CHAT_WINDOW, data);
    const accountId = resolveActionAccountId(rootGetters);

    let conversationInStore = state.allConversations.find((c) =>
      areSameConversationId(c.id, data.id)
    );
    if (!conversationInStore && data?.id) {
      commit(types.UPDATE_CONVERSATION, data);
      conversationInStore = state.allConversations.find((c) =>
        areSameConversationId(c.id, data.id)
      );
    }
    const conversationForLoadState = conversationInStore || data;
    const alreadyFetched = hasLoadedMessagesForActiveConversation(
      conversationForLoadState
    );
    const hasMissingDeepLinkTarget =
      after !== null &&
      after !== undefined &&
      after !== '' &&
      !hasRenderableConversationMessageId(conversationForLoadState, after);

    if (!alreadyFetched || hasMissingDeepLinkTarget) {
      await dispatch('ensureConversationMessagesLoaded', {
        accountId,
        after,
        conversationId: data.id,
        force: hasMissingDeepLinkTarget,
      });
    } else {
      // Thread già in store ma il preview della lista può essere più nuovo:
      // recupera solo la coda mancante in background, l'apertura resta
      // istantanea (l'action è no-op se la coda è già allineata).
      scheduleConversationTailRevalidation({
        accountId,
        conversationId: data.id,
        dispatch,
        state,
      });
    }
  },

  assignAgent: async ({ dispatch }, { conversationId, agentId }) => {
    try {
      const response = await ConversationApi.assignAgent({
        conversationId,
        agentId,
      });
      dispatch('setCurrentChatAssignee', response.data);
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatAssignee({ commit }, assignee) {
    commit(types.ASSIGN_AGENT, assignee);
  },

  assignGmailLabels: async (
    _,
    {
      conversationId,
      targetLabelId,
      targetLabelTitle,
      inboxId,
      currentLabelId = null,
    }
  ) => {
    const response = await ConversationApi.assignGmailLabels({
      conversationId,
      targetLabelId,
      targetLabelTitle,
      inboxId,
      currentLabelId,
    });

    // Trigger refresh delle label per aggiornare i conteggi
    dispatchSafeWindowCustomEvent('gmail-labels-reload', {
      conversationId,
      inboxId,
    });

    return response.data;
  },

  assignMicrosoftFolders: async (
    _,
    {
      conversationId,
      targetFolderId,
      targetFolderTitle,
      inboxId,
      currentFolderId = null,
    }
  ) => {
    const response = await ConversationApi.assignMicrosoftFolders({
      conversationId,
      targetFolderId,
      targetFolderTitle,
      inboxId,
      currentFolderId,
    });

    // Trigger refresh delle folder per aggiornare i conteggi
    dispatchSafeWindowCustomEvent('microsoft-folders-reload', {
      conversationId,
      inboxId,
    });

    return response.data;
  },

  assignImapFolders: async (
    _,
    {
      conversationId,
      targetFolderId,
      targetFolderTitle,
      inboxId,
      currentFolderId = null,
    }
  ) => {
    const response = await ConversationApi.assignImapFolders({
      conversationId,
      targetFolderId,
      targetFolderTitle,
      inboxId,
      currentFolderId,
    });
    return response.data;
  },

  toggleStatus: async (
    { commit, state, rootGetters },
    { conversationId, status, snoozedUntil = null, conversationSnapshot = null }
  ) => {
    const conversation =
      conversationSnapshot ||
      getConversationById(state, conversationId) ||
      (areSameConversationId(state.selectedChat?.id, conversationId)
        ? state.selectedChat
        : null);
    const inbox = rootGetters?.['inboxes/getInbox']?.(conversation?.inbox_id);
    const conversationArchive =
      isConversationArchiveInbox(inbox) &&
      ['open', 'resolved'].includes(status);
    const expectedIncomingId = conversation?.archive_version;
    commit(types.SET_CONVERSATION_PLACEMENT_OPERATION, {
      id: conversationId,
      kind: 'status',
      removeFromList: true,
      status,
    });

    try {
      const {
        data: {
          payload: {
            current_status: updatedStatus,
            snoozed_until: updatedSnoozedUntil,
          } = {},
        } = {},
      } = await (conversationArchive
        ? ConversationApi.setArchived({
            conversationId,
            status,
            expectedIncomingId,
          })
        : ConversationApi.toggleStatus({
            conversationId,
            status,
            snoozedUntil,
          }));
      const latestConversation =
        getConversationById(state, conversationId) || state.selectedChat;
      // An incoming event can arrive before this HTTP response. Keep the newer
      // state instead of hiding a reply already delivered over the websocket.
      const newerIncoming =
        conversationArchive &&
        status === 'resolved' &&
        areSameConversationId(latestConversation?.id, conversationId) &&
        latestConversation.archive_version > expectedIncomingId;
      const nextStatus = newerIncoming
        ? latestConversation.status
        : updatedStatus || status;
      const nextSnoozedUntil =
        updatedSnoozedUntil === undefined ? snoozedUntil : updatedSnoozedUntil;

      // 🎯 PROTEZIONE: Se si sta archiviando la conversazione attualmente selezionata,
      // deselezionala per evitare errori di rendering
      if (
        nextStatus === 'resolved' &&
        areSameConversationId(state.selectedChatId, conversationId) &&
        state.currentInbox !== 0 // Non deselezionare se siamo nella vista "Tutte le conversazioni"
      ) {
        commit(types.CLEAR_CURRENT_CHAT_WINDOW);
      }

      commit(types.CHANGE_CONVERSATION_STATUS, {
        conversationId,
        status: nextStatus,
        snoozedUntil: nextSnoozedUntil,
      });
      if (conversationArchive) {
        commit('INVALIDATE_CACHE_FOR_INBOX', conversation.inbox_id);
        commit(types.CLEAR_CONVERSATION_PLACEMENT_OPERATION, {
          id: conversationId,
        });
      }
    } catch (error) {
      commit(types.CLEAR_CONVERSATION_PLACEMENT_OPERATION, {
        id: conversationId,
      });
      throw error;
    }
  },

  createPendingMessageAndSend: async ({ dispatch, rootGetters }, data) => {
    const accountId = resolveActionAccountId(rootGetters, data?.accountId);
    const pendingMessage = createPendingMessage({ ...data, accountId });
    // Propaga la Promise reale dell'invio API:
    // i chiamanti possono attendere successo/errore invece di ricevere un falso positivo.
    return dispatch('sendMessageWithData', pendingMessage);
  },

  sendMessageWithData: async (
    { commit, dispatch, rootGetters, state },
    pendingMessage
  ) => {
    const { conversation_id: conversationId, id } = pendingMessage;
    const accountId = resolveActionAccountId(
      rootGetters,
      pendingMessage.accountId
    );
    // Capture placement before the send starts. If the user moves, archives,
    // or deletes the conversation while the POST is in flight, the detached
    // revalidation must not apply a snapshot from before that operation.
    const expectedPlacementOperation = getConversationPlacementOperation(
      state,
      conversationId
    );
    const accountContextIsCurrent = () =>
      accountId == null || isCurrentAccountContext(rootGetters, accountId);
    let response;
    try {
      if (accountContextIsCurrent()) {
        commit(types.ADD_MESSAGE, {
          ...pendingMessage,
          status: MESSAGE_STATUS.PROGRESS,
        });
      }
      response = hasMessageFailedWithExternalError(pendingMessage)
        ? await MessageApi.retry(conversationId, id)
        : await MessageApi.create(pendingMessage);
    } catch (error) {
      const errorMessage = error.response
        ? error.response.data.error
        : undefined;
      if (accountContextIsCurrent()) {
        commit(types.ADD_MESSAGE, {
          ...pendingMessage,
          meta: {
            error: errorMessage,
          },
          status: MESSAGE_STATUS.FAILED,
        });
      }
      throw error;
    }

    // The request belongs to the captured account even if routing changed
    // while it was in flight. Return its result to the caller, but never
    // insert it into the newly selected account's shared conversation store.
    if (!accountContextIsCurrent()) {
      return response.data;
    }

    commit(types.ADD_MESSAGE, {
      ...response.data,
      status: MESSAGE_STATUS.SENT,
    });
    commit(types.ADD_CONVERSATION_ATTACHMENTS, {
      ...response.data,
      status: MESSAGE_STATUS.SENT,
    });
    // The API accepted the send. Cache/list revalidation is best-effort and
    // must not keep the composer open after the POST has succeeded. A refresh
    // failure must never turn an accepted message into FAILED or make the
    // composer invite the user to submit the same email again.
    try {
      messagePrefetchCache.invalidateConversation({
        accountId,
        conversationId,
      });
      if (!accountContextIsCurrent()) return response.data;
      await dispatch('clearConversationCache');
      if (!accountContextIsCurrent()) return response.data;
      Promise.resolve(
        dispatch('getConversation', {
          accountId,
          conversationId,
          expectedPlacementOperation,
          forceAdd: true,
        })
      ).catch(() => {});
    } catch {
      // Realtime/provider updates will reconcile the conversation later.
    }

    return response.data;
  },

  addMessage({ commit }, message) {
    commit(types.ADD_MESSAGE, message);
    if (message.message_type === MESSAGE_TYPE.INCOMING) {
      commit(types.SET_CONVERSATION_CAN_REPLY, {
        conversationId: message.conversation_id,
        canReply: true,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, message);
    }
  },

  updateMessage({ commit, rootGetters }, message) {
    commit(types.ADD_MESSAGE, message);
    const conversationId =
      message?.conversation_id ?? message?.conversation?.id ?? null;
    if (conversationId != null) {
      messagePrefetchCache.invalidateConversation({
        accountId: resolveActionAccountId(rootGetters),
        conversationId,
      });
    }
  },

  deleteMessage: async function deleteLabels(
    { commit, rootGetters },
    { conversationId, messageId }
  ) {
    try {
      const { data } = await MessageApi.delete(conversationId, messageId);
      commit(types.ADD_MESSAGE, data);
      commit(types.DELETE_CONVERSATION_ATTACHMENTS, data);
      messagePrefetchCache.invalidateConversation({
        accountId: resolveActionAccountId(rootGetters),
        conversationId,
      });
    } catch (error) {
      throw new Error(error);
    }
  },

  removeResolvedConversations: async ({ commit }, resolvedIds) => {
    try {
      commit(types.REMOVE_RESOLVED_CONVERSATIONS, resolvedIds);
    } catch (error) {
      // Errore durante la rimozione delle conversazioni risolte
    }
  },

  addConversation({ state, dispatch, rootGetters }, conversation) {
    const { currentInbox } = state;
    const inboxId =
      conversation.inbox_id ??
      conversation.inboxId ??
      conversation.meta?.inbox_id ??
      conversation.meta?.inboxId ??
      null;
    const conversationFilters = state.conversationFilters || {};
    if (
      shouldRemoveConversationForLabelFilter(conversationFilters, conversation)
    ) {
      const conversationInboxId =
        conversation.inbox_id ??
        conversation.inboxId ??
        conversation.meta?.inbox_id ??
        conversation.meta?.inboxId ??
        state.currentInbox ??
        null;

      if (conversationInboxId) {
        dispatch('clearConversationCache', { inboxId: conversationInboxId });
      }
      return;
    }

    const hasSpecificInbox =
      currentInbox !== null &&
      currentInbox !== undefined &&
      String(currentInbox) !== '0';
    const isMatchingInboxFilter =
      !hasSpecificInbox ||
      inboxId === null ||
      inboxId === undefined ||
      Number(currentInbox) === Number(inboxId);

    if (!isMatchingInboxFilter) {
      return;
    }

    // Dashboard: escludi canali API dalla vista generale.
    if (
      shouldBlockExcludedChannelOnDashboard(state, conversation, rootGetters)
    ) {
      return;
    }

    // Dashboard: email senza label "inbox" non vanno aggiunte
    if (shouldBlockEmailOnDashboard(state, conversation, rootGetters)) {
      return;
    }

    dispatch('updateConversation', conversation);
  },

  addUnattended({ dispatch, rootState }, conversation) {
    if (isOnUnattendedView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  updateConversation({ state, commit, dispatch, rootGetters }, conversation) {
    if (conversation.archive_version != null && conversation.inbox_id) {
      commit('INVALIDATE_CACHE_FOR_INBOX', conversation.inbox_id);
    }
    // 🛡️ PROTEZIONE: Gestisce il caso in cui meta sia undefined
    // Questo può accadere quando il WebSocket invia un payload incompleto
    // (es. dopo invio risposta email)
    const meta = conversation.meta || {};
    const { sender } = meta;

    // 🚦 GATE: Se la conversazione NON esiste nello store,
    // verifica che corrisponda ai filtri attivi prima di aggiungerla.
    // Questo previene il "flash" di conversazioni non pertinenti (es. WhatsApp in vista Gmail).
    const existingConversation = state.allConversations.find((c) =>
      areSameConversationId(c.id, conversation.id)
    );
    const existsInStore = !!existingConversation;
    const pendingPlacementOperation =
      state.pendingPlacementOperations?.[String(conversation.id)] || null;
    const { __placement_source: placementSource } = conversation;
    const resolvedPlacementSource =
      placementSource || CONVERSATION_PLACEMENT_SOURCE.FETCH;
    if (
      shouldSuppressRealtimeConversationPlacementForPage({
        chatSortFilter: state.chatSortFilter,
        currentConversation: existingConversation,
        currentPage: state.conversationFilters?.page,
        source: resolvedPlacementSource,
      }) ||
      shouldSuppressConversationPlacementUpdate({
        incomingConversation: conversation,
        operation: pendingPlacementOperation,
        source: resolvedPlacementSource,
      })
    ) {
      return;
    }

    if (
      !existsInStore &&
      !shouldAddConversationToList(state, conversation, rootGetters)
    ) {
      // La conversazione non appartiene alla vista corrente, non aggiungerla
      return;
    }

    // 🔧 FIX: Se la conversazione esiste già e il nuovo payload NON ha label data,
    // preserva i label esistenti. Questo evita che un secondo evento (es. da getConversation API)
    // con dati incompleti sovrascriva i label ricevuti dal primo evento WebSocket.
    if (existsInStore && existingConversation) {
      const newHasLabelIds =
        Array.isArray(conversation.label_ids) &&
        conversation.label_ids.length > 0;
      const newHasLabels =
        Array.isArray(conversation.labels) && conversation.labels.length > 0;
      const existingHasLabelIds =
        Array.isArray(existingConversation.label_ids) &&
        existingConversation.label_ids.length > 0;
      const existingHasLabels =
        Array.isArray(existingConversation.labels) &&
        existingConversation.labels.length > 0;

      if (!newHasLabelIds && existingHasLabelIds) {
        conversation.label_ids = existingConversation.label_ids;
      }
      if (!newHasLabels && existingHasLabels) {
        conversation.labels = existingConversation.labels;
      }
    }

    // Normalize labels before updating store and dependent modules
    const normalizedLabels = getLabelsFromConversation(conversation);
    if (normalizedLabels && Array.isArray(normalizedLabels)) {
      conversation = { ...conversation, labels: normalizedLabels };
    }
    commit(types.UPDATE_CONVERSATION, conversation);

    dispatch('conversationLabels/setConversationLabel', {
      id: conversation.id,
      data: conversation.labels,
    });

    // 🛡️ PROTEZIONE: Chiama setContact solo se sender è definito
    if (sender) {
      dispatch('contacts/setContact', sender);
    }

    if (
      conversation?.id &&
      shouldRemoveConversationForLabelFilter(
        state.conversationFilters,
        conversation
      )
    ) {
      commit(types.REMOVE_RESOLVED_CONVERSATIONS, [conversation.id]);

      const conversationInboxId =
        conversation.inbox_id ??
        conversation.inboxId ??
        conversation.meta?.inbox_id ??
        conversation.meta?.inboxId ??
        state.currentInbox ??
        null;

      if (conversationInboxId) {
        dispatch('clearConversationCache', { inboxId: conversationInboxId });
      }
    }
  },

  updateConversationLastActivity(
    { commit },
    { conversationId, lastActivityAt }
  ) {
    commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
      lastActivityAt,
      conversationId,
    });
  },

  // typing_on/typing_off da actionCable: la mutation non puo' leggere gli
  // altri moduli, quindi qui si raccolgono inbox e record di typing; la regola
  // (Otello al lavoro su una inbox API = riga ferma) sta nell'helper puro.
  // inboxId viene dal payload del cable: la conversazione puo' non essere
  // ancora in lista (typing_on prima di conversation.created).
  syncConversationPlacementFreeze(
    { commit, rootGetters, state },
    { conversationId = null, inboxId = null } = {}
  ) {
    if (conversationId === null || conversationId === undefined) return;

    const resolvedInboxId =
      inboxId ??
      state.allConversations.find((conversation) =>
        areSameConversationId(conversation.id, conversationId)
      )?.inbox_id ??
      null;
    const inbox =
      resolvedInboxId === null
        ? null
        : rootGetters['inboxes/getInbox'](resolvedInboxId);
    const typingUsersList =
      rootGetters['conversationTypingStatus/getUserList'](conversationId);

    commit(types.SYNC_CONVERSATION_PLACEMENT_FREEZE, {
      conversationId,
      inbox,
      typingUsersList,
    });
  },

  setChatStatusFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_STATUS_FILTER, data);
  },

  setChatSortFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_SORT_FILTER, data);
  },

  setUnreadFirst({ commit }, enabled) {
    commit(types.SET_UNREAD_FIRST, enabled);
  },

  setPriorityFilter({ commit }, enabled) {
    commit(types.SET_PRIORITY_FILTER, enabled);
  },

  updateAssignee({ commit }, data) {
    commit(types.UPDATE_ASSIGNEE, data);
  },

  updateConversationContact({ commit }, data) {
    if (data.id) {
      commit(`contacts/${types.SET_CONTACT_ITEM}`, data);
    }
    commit(types.UPDATE_CONVERSATION_CONTACT, data);
  },

  setActiveInbox({ commit }, inboxId) {
    commit(types.SET_ACTIVE_INBOX, inboxId);
  },

  // ✨ NUOVA AZIONE: Imposta l'inbox attiva con informazioni sul filtro per il pageTitle
  setActiveInboxWithFilter(
    { commit },
    { inboxId, inboxName, filterType, filterValue, filterLabel }
  ) {
    commit(types.SET_ACTIVE_INBOX, inboxId);
    commit(types.SET_ACTIVE_INBOX_FILTER, {
      inboxId,
      inboxName,
      filterType,
      filterValue,
      filterLabel,
    });
  },

  muteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.mute(conversationId);
      commit(types.MUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  unmuteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.unmute(conversationId);
      commit(types.UNMUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  sendEmailTranscript: async (_, { conversationId, email }) => {
    try {
      await ConversationApi.sendEmailTranscript({ conversationId, email });
    } catch (error) {
      throw new Error(error);
    }
  },

  updateCustomAttributes: async (
    { commit },
    { conversationId, customAttributes }
  ) => {
    try {
      const response = await ConversationApi.updateCustomAttributes({
        conversationId,
        customAttributes,
      });
      const { custom_attributes } = response.data;
      commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, custom_attributes);
    } catch (error) {
      // Handle error
    }
  },

  setConversationFilters({ commit }, data) {
    commit(types.SET_CONVERSATION_FILTERS, data);
  },

  clearConversationFilters({ commit }) {
    commit(types.CLEAR_CONVERSATION_FILTERS);
  },

  setChatListFilters({ commit }, data) {
    commit(types.SET_CHAT_LIST_FILTERS, data);
  },

  updateChatListFilters({ commit }, data) {
    commit(types.UPDATE_CHAT_LIST_FILTERS, data);
  },

  assignPriority: async ({ dispatch }, { conversationId, priority }) => {
    try {
      await ConversationApi.togglePriority({
        conversationId,
        priority,
      });

      dispatch('setCurrentChatPriority', {
        priority,
        conversationId,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatPriority({ commit }, { priority, conversationId }) {
    commit(types.ASSIGN_PRIORITY, { priority, conversationId });
  },

  setContextMenuChatId({ commit }, chatId) {
    commit(types.SET_CONTEXT_MENU_CHAT_ID, chatId);
  },

  ...messageReadActions,

  clearSelectedConversation({ commit }) {
    commit(types.CLEAR_CURRENT_CHAT_WINDOW);
  },

  refreshConversations({ dispatch, state }) {
    const { filter, conversationStatus } = state;
    const params = {
      filter,
      conversationStatus,
      page: 1,
    };

    return dispatch('fetchAllConversations', params);
  },

  updateArchivedConversations({ dispatch }) {
    return dispatch('fetchAllConversations', {
      conversationStatus: 'resolved',
      page: 1,
    });
  },

  checkAndUpdateArchivedView({ dispatch, state, rootState }) {
    const currentRoute = rootState.route ? rootState.route.name : '';
    const filters = state.chatListFilter || {};
    const isInArchivedView =
      currentRoute &&
      (currentRoute.includes('archived') ||
        filters.conversationStatus === 'resolved');

    if (isInArchivedView) {
      return dispatch('fetchAllConversations', {
        conversationStatus: 'resolved',
        page: 1,
      });
    }

    return Promise.resolve();
  },

  // ✨ NUOVA AZIONE: Ottimizzazione intelligente del cache basata sul tipo di canale
  optimizeInboxCache: (
    { commit, rootGetters, state },
    { inboxId, forceRefresh = false }
  ) => {
    try {
      const inbox = rootGetters['inboxes/getInbox'](inboxId);
      const inboxType = inbox?.channel_type || 'unknown';

      // Ottimizzazione basata sul tipo di canale
      const isRealTimeChannel = [
        WHATSAPP_CONVERSATION_CHANNEL_TYPE,
        'Channel::Telegram',
      ].includes(inboxType);
      const isEmailChannel = inboxType.includes('Email');
      const isVoiceChannel = ['Channel::Voice', 'Channel::VideoCall'].includes(
        inboxType
      );

      if (forceRefresh || isVoiceChannel) {
        // Per canali vocali o quando forzato, invalida tutto il cache
        commit('INVALIDATE_CACHE_FOR_INBOX', inboxId);
      } else if (isRealTimeChannel) {
        // Per canali real-time, mantieni solo cache recente (< 2 minuti)
        const cutoffTime = Date.now() - 2 * 60 * 1000;

        // Controllo sicurezza per il cache
        if (state.conversationCache) {
          Object.keys(state.conversationCache).forEach((key) => {
            if (
              isConversationCacheOptimizationKeyForInbox({
                cacheKey: key,
                inboxId,
                inboxType,
              })
            ) {
              const cache = state.conversationCache[key];
              if (cache && cache.timestamp < cutoffTime) {
                commit('CLEAR_CONVERSATION_CACHE', key);
              }
            }
          });
        }
      } else if (isEmailChannel) {
        // Per canali email, mantieni cache più a lungo ma pulisci quello molto vecchio
        const cutoffTime = Date.now() - 60 * 60 * 1000; // 1 ora

        // Controllo sicurezza per il cache
        if (state.conversationCache) {
          Object.keys(state.conversationCache).forEach((key) => {
            if (key.startsWith(`${inboxId}:`)) {
              const cache = state.conversationCache[key];
              if (cache && cache.timestamp < cutoffTime) {
                commit('CLEAR_CONVERSATION_CACHE', key);
              }
            }
          });
        }
      }

      // Pulizia generale del cache scaduto
      commit('CLEAR_EXPIRED_CONVERSATION_CACHE');
    } catch (error) {
      // Errore nell'ottimizzazione cache per inbox
    }
  },

  // ✨ NUOVA AZIONE: Precaricamento intelligente per inbox multiple
  preloadMultipleInboxes: async (
    { dispatch, rootGetters },
    { inboxIds, priority = 'normal' }
  ) => {
    const preloadPromises = inboxIds.map(async (inboxId) => {
      const inbox = rootGetters['inboxes/getInbox'](inboxId);
      const inboxType = inbox?.channel_type || 'unknown';

      // Priorità di precaricamento basata sul tipo
      const isHighPriority = [
        'Channel::Voice',
        'Channel::VideoCall',
        WHATSAPP_CONVERSATION_CHANNEL_TYPE,
      ].includes(inboxType);
      const delay =
        priority === 'high' || isHighPriority ? 0 : Math.random() * 2000; // Stagger loading

      await waitForDelay(delay);

      const defaultFilters = {
        page: 1,
        status: 'open',
        inboxId: inboxId,
        assigneeType: 'all',
        sortBy: 'last_activity_at_desc',
      };

      try {
        await dispatch('fetchCachedConversations', {
          inboxId,
          filters: defaultFilters,
          inboxType,
        });
        // Precaricamento completato per inbox
      } catch (error) {
        // Precaricamento fallito per inbox
      }
    });

    return Promise.allSettled(preloadPromises);
  },

  // ✨ NUOVA AZIONE: Monitoraggio performance cache
  getCachePerformanceReport: ({ getters }) => {
    try {
      // Controlla se i getters esistono
      if (
        typeof getters.getCacheStatistics !== 'function' ||
        typeof getters.getCacheHealth !== 'function'
      ) {
        // Getters getCacheStatistics o getCacheHealth non disponibili
        return {
          timestamp: new Date().toISOString(),
          totalCacheEntries: 0,
          health: { status: 'unknown', issues: [], recommendations: [] },
          performance: {
            hitRatio: 0,
            averageConversationsPerEntry: 0,
            recommendedActions: [],
          },
        };
      }

      const stats = getters.getCacheStatistics();
      const health = getters.getCacheHealth();

      const report = {
        timestamp: new Date().toISOString(),
        ...stats,
        health,
        performance: {
          hitRatio: stats.totalCacheEntries > 0 ? health.efficiency / 100 : 0,
          averageConversationsPerEntry:
            stats.totalCacheEntries > 0
              ? Math.round(
                  stats.totalConversationsCached / stats.totalCacheEntries
                )
              : 0,
          recommendedActions: health.recommendations || [],
        },
      };

      // Log report in development
      if (process.env.NODE_ENV === 'development') {
        // Cache Performance Report logging removed
      }
      return report;
    } catch (error) {
      // Errore nella generazione del report performance
      return {
        timestamp: new Date().toISOString(),
        totalCacheEntries: 0,
        health: {
          status: 'error',
          issues: [error.message],
          recommendations: [],
        },
        performance: {
          hitRatio: 0,
          averageConversationsPerEntry: 0,
          recommendedActions: [],
        },
      };
    }
  },

  // ✨ NUOVA AZIONE: Auto-ottimizzazione periodica
  scheduleAutoOptimization: ({ dispatch }) => {
    if (autoOptimizationInterval) {
      return autoOptimizationInterval;
    }

    // Ottimizzazione ogni 5 minuti
    autoOptimizationInterval = scheduleSafeInterval(
      () => {
        try {
          dispatch('clearExpiredConversationCache');

          // Report performance ogni 15 minuti
          if (Date.now() % (15 * 60 * 1000) < 5000) {
            dispatch('getCachePerformanceReport');
          }
        } catch (error) {
          // Errore durante auto-ottimizzazione
        }
      },
      5 * 60 * 1000
    );

    if (!autoOptimizationInterval) {
      return null;
    }

    // Cleanup su page unload
    if (typeof window !== 'undefined') {
      autoOptimizationCleanup = () => {
        clearSafeInterval(autoOptimizationInterval);
        autoOptimizationInterval = null;
        if (autoOptimizationCleanup) {
          removeSafeEventListener(
            window,
            'beforeunload',
            autoOptimizationCleanup
          );
        }
        autoOptimizationCleanup = null;
      };
      addSafeEventListener(window, 'beforeunload', autoOptimizationCleanup);
    }

    return autoOptimizationInterval;
  },

  // Actions universali per il cache delle conversazioni
  fetchCachedConversations: async (
    { commit, getters, dispatch, rootGetters },
    { inboxId, filters, inboxType = null, forceRefresh = false, signal = null }
  ) => {
    const throwIfAborted = () => {
      if (signal && signal.aborted) {
        const error = new Error('Conversation request aborted');
        error.name = 'AbortError';
        throw error;
      }
    };

    // Ottieni il tipo di inbox se non fornito
    if (!inboxType) {
      const inbox = rootGetters['inboxes/getInbox'](inboxId);
      inboxType = inbox?.channel_type || 'unknown';
    }

    // 🆕 NORMALIZZA i filtri prima di generare la cache key
    const normalizedFilters = {
      ...filters,
      inboxId: inboxId || null,
      page: filters.page || 1,
      per_page: filters.per_page || 30,
    };

    // Genera chiave di cache con filtri normalizzati
    const cacheKey = getters.generateCacheKey(inboxId, normalizedFilters);

    // 🆕 PULIZIA CACHE SCADUTO all'inizio
    commit('CLEAR_EXPIRED_CONVERSATION_CACHE');
    throwIfAborted();

    // 🔧 FUNZIONE HELPER: Carica dal cache
    const loadFromCache = async (conversationCache) => {
      try {
        throwIfAborted();

        // Imposta lo stato di loading brevemente per mostrare il cambio
        commit(types.SET_LIST_LOADING_STATUS);

        // Piccolo delay per permettere all'UI di aggiornarsi
        await waitForDelay(50);
        throwIfAborted();

        // 🧹 PULIZIA COMPLETA: Forza il reset delle conversazioni per evitare mixing
        commit(types.EMPTY_ALL_CONVERSATION);

        // Aggiorna i filtri con quelli del cache (che dovrebbero essere identici)
        commit(types.UPDATE_CHAT_LIST_FILTERS, conversationCache.filters);

        // Carica le conversazioni dal cache
        const cachedConversations = cloneConversationCacheList(
          conversationCache.conversations
        );
        commit(types.SET_ALL_CONVERSATION, cachedConversations);

        // 🔢 Aggiorna il totale filtrato senza azzerare i contatori non presenti.
        try {
          const statsMeta =
            buildConversationStatsMetaFromCache(conversationCache);
          if (statsMeta) {
            commit(
              `conversationStats/${types.UPDATE_CONVERSATION_STATS}`,
              statsMeta,
              { root: true }
            );
          }
        } catch (_) {
          // Cache-derived stats are best effort.
        }
        // Pulisce lo stato di loading
        commit(types.CLEAR_LIST_LOADING_STATUS);
        return cachedConversations;
      } catch (error) {
        commit(types.CLEAR_LIST_LOADING_STATUS);
        throw error;
      }
    };

    // 🔧 FUNZIONE HELPER: Fetch dal server
    const fetchFromServer = async () => {
      try {
        throwIfAborted();
        // ⚡ OTTIMIZZAZIONE: Imposta subito lo stato di loading
        commit(types.SET_LIST_LOADING_STATUS);

        // 🧹 PULIZIA: Reset pagina e filtri
        await dispatch('conversationPage/reset');
        await dispatch('updateChatListFilters', normalizedFilters);

        // 🗑️ NOTA: NON svuotiamo qui - fetchAllConversations lo farà al momento giusto
        // dopo aver ricevuto i dati, evitando il flickering
        throwIfAborted();

        // 📡 FETCH: Ottieni dal server
        const result = await dispatch('fetchAllConversations', {
          ...normalizedFilters,
          signal,
        });
        throwIfAborted();

        // 📊 CACHE: Salva i risultati nel cache
        const currentConversations = getters.getAllConversations;

        // Prova ad estrarre il totale dal risultato del server
        const metaFromServer = result?.data?.meta || null;
        const totalFromServer =
          metaFromServer && metaFromServer.all_count !== undefined
            ? metaFromServer.all_count
            : null;

        commit('SET_CONVERSATION_CACHE', {
          cacheKey,
          conversations: currentConversations,
          filters: normalizedFilters,
          inboxType,
          totalCount: totalFromServer,
          meta: clonePlainValue(metaFromServer),
        });
        return result;
      } catch (error) {
        // Errore nel fetch
        commit(types.CLEAR_LIST_LOADING_STATUS);
        throw error;
      }
    };

    // 🔧 FUNZIONE HELPER: Confronto profondo dei filtri
    const deepCompareFilters = (cacheFilters, requestedFilters) => {
      return (
        JSON.stringify(normalizeConversationFiltersForCompare(cacheFilters)) ===
        JSON.stringify(normalizeConversationFiltersForCompare(requestedFilters))
      );
    };

    if (forceRefresh) {
      return fetchFromServer();
    }

    // Controlla se esiste un cache valido
    const hasValidCache = getters.isConversationCacheValid(cacheKey, inboxType);
    const cache = getters.getConversationCache(cacheKey);

    if (hasValidCache && cache) {
      // 🎯 VERIFICA che i filtri nel cache corrispondano esattamente a quelli richiesti
      const cacheFiltersMatch = deepCompareFilters(
        cache.filters,
        normalizedFilters
      );

      if (!cacheFiltersMatch) {
        commit('CLEAR_CONVERSATION_CACHE', cacheKey);
        return fetchFromServer();
      }

      // ✅ Cache valido e filtri corrispondenti, carica dal cache
      return loadFromCache(cache);
    }

    return fetchFromServer();
  },

  clearConversationCache: (
    { commit, getters },
    { inboxId = null, filters = null } = {}
  ) => {
    if (inboxId && filters) {
      const cacheKey = getters.generateCacheKey(inboxId, filters);
      commit('CLEAR_CONVERSATION_CACHE', cacheKey);
    } else if (inboxId) {
      // Pulisce tutto il cache per una specifica inbox
      commit('INVALIDATE_CACHE_FOR_INBOX', inboxId);
    } else {
      // Pulisce tutto il cache
      commit('CLEAR_CONVERSATION_CACHE');
    }
  },

  clearExpiredConversationCache: ({ commit }) => {
    commit('CLEAR_EXPIRED_CONVERSATION_CACHE');
  },

  // Polling ibrido: sempre fetcha dal server, aggiorna cache se necessario
  silentRefreshWithCache: async (
    { commit, getters, dispatch, rootGetters },
    { inboxId, filters, inboxType = null }
  ) => {
    if (!inboxType) {
      const inbox = rootGetters['inboxes/getInbox'](inboxId);
      inboxType = inbox?.channel_type || 'unknown';
    }

    // Fetch sempre dal server per il polling
    await dispatch('updateChatListFilters', filters);
    const result = await dispatch('fetchAllConversations');

    // Ottieni conversazioni aggiornate
    const currentConversations = getters.getAllConversations;

    // Aggiorna il cache con i dati freschi
    const cacheKey = getters.generateCacheKey(inboxId, filters);
    commit('SET_CONVERSATION_CACHE', {
      cacheKey,
      conversations: currentConversations,
      filters,
      inboxType,
      meta: clonePlainValue(result?.data?.meta),
    });

    return result;
  },

  // 🆕 NUOVO: Invalidazione intelligente del cache quando cambia contesto
  // Ispirato al sistema di Zero con connectionId
  invalidateCacheOnContextChange: (
    { commit, getters },
    { oldContext, newContext, reason = 'context_change' }
  ) => {
    const cacheKeysToInvalidate = [];

    // 1️⃣ Se cambia inbox, invalida tutto il cache di quella inbox
    if (oldContext?.inboxId !== newContext?.inboxId) {
      if (oldContext?.inboxId) {
        cacheKeysToInvalidate.push(`inbox:${oldContext.inboxId}`);
      }
      if (newContext?.inboxId) {
        cacheKeysToInvalidate.push(`inbox:${newContext.inboxId}`);
      }
    }

    // 2️⃣ Se cambia label nella stessa inbox, invalida solo il cache di quella label
    if (
      oldContext?.inboxId === newContext?.inboxId &&
      oldContext?.label !== newContext?.label
    ) {
      const inboxId = oldContext.inboxId || newContext.inboxId;
      if (inboxId) {
        // Invalida sia la vecchia che la nuova label
        if (oldContext.label) {
          const oldCacheKey = getters.generateCacheKey(inboxId, {
            ...oldContext,
            labels: oldContext.label,
          });
          cacheKeysToInvalidate.push(oldCacheKey);
        }
        if (newContext.label) {
          const newCacheKey = getters.generateCacheKey(inboxId, {
            ...newContext,
            labels: newContext.label,
          });
          cacheKeysToInvalidate.push(newCacheKey);
        }
      }
    }

    // 3️⃣ Se cambia status nella stessa inbox, invalida cache correlati
    if (
      oldContext?.inboxId === newContext?.inboxId &&
      oldContext?.status !== newContext?.status
    ) {
      const inboxId = oldContext.inboxId || newContext.inboxId;
      if (inboxId) {
        // Invalida sia il vecchio che il nuovo status
        if (oldContext.status) {
          const oldCacheKey = getters.generateCacheKey(inboxId, {
            ...oldContext,
            status: oldContext.status,
          });
          cacheKeysToInvalidate.push(oldCacheKey);
        }
        if (newContext.status) {
          const newCacheKey = getters.generateCacheKey(inboxId, {
            ...newContext,
            status: newContext.status,
          });
          cacheKeysToInvalidate.push(newCacheKey);
        }
      }
    }

    // 4️⃣ ESECUZIONE: Invalida le cache key individuate
    const uniqueCacheKeys = [...new Set(cacheKeysToInvalidate)];

    uniqueCacheKeys.forEach((key) => {
      if (key.startsWith('inbox:')) {
        // Invalida per inbox ID
        const inboxId = key.split(':')[1];
        commit('INVALIDATE_CACHE_FOR_INBOX', inboxId);
      } else {
        // Invalida per cache key specifica
        commit('CLEAR_CONVERSATION_CACHE', key);
      }
    });

    // 5️⃣ PULIZIA GENERALE: Rimuovi cache scaduto
    commit('CLEAR_EXPIRED_CONVERSATION_CACHE');

    return {
      invalidatedKeys: uniqueCacheKeys,
      reason,
    };
  },
};

export default actions;
